import React, { useState, useEffect, useCallback } from 'react';
import { getWorkingDaysData, updateWorkingDaysData } from '../../../api/finance-api';
import TanstackTable from '../../../components/TanstackTable';
import { ColumnDef } from "@tanstack/react-table";
import Breadcrums from "../../../components/breadcrums";
import PageTitle from "../../../components/pageTitle";
import { paymentsCommands, sectionClass } from "../../../constants/common";

interface WorkingDaysData {
    uuid: string;
    month_year: string;
    working_days: number;
    employee_name: string;
}

interface EditingCell {
    uuid: string;
    month_year: string;
    value: number;
}

const WorkingDays: React.FC = () => {
    const [workingDaysData, setWorkingDaysData] = useState<WorkingDaysData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const handleEdit = useCallback((rowData: WorkingDaysData) => {
        console.log('Edit clicked for:', rowData);
        setEditingCell({
            uuid: rowData.uuid,
            month_year: rowData.month_year,
            value: Number(rowData.working_days) || 0
        });
    }, []);

    const handleCancel = useCallback(() => {
        console.log('Cancel clicked');
        setEditingCell(null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!editingCell) return;
        console.log('Save clicked for:', editingCell);

        try {
            setIsSaving(true);
            setError(null);
            
            await updateWorkingDaysData(editingCell.uuid, editingCell.month_year, editingCell.value);
            
            // Update local state
            setWorkingDaysData(prevData => 
                prevData.map(item => 
                    item.uuid === editingCell.uuid && item.month_year === editingCell.month_year
                        ? { ...item, working_days: editingCell.value }
                        : item
                )
            );
            
            setEditingCell(null);
            console.log('Working days updated successfully');
        } catch (error: any) {
            console.error('Error updating working days:', error);
            setError(`Failed to update working days: ${error?.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    }, [editingCell]);

    // Define columns for TanstackTable
    const columns = React.useMemo<ColumnDef<WorkingDaysData, any>[]>(
        () => [
            {
                accessorKey: 'employee_name',
                header: 'Employee Name',
                cell: ({ row }) => (
                    <span className="font-semibold">{row.original.employee_name || 'Unknown'}</span>
                ),
                enableColumnFilter: true,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-left",
                },
                footer: ({ table }) => (
                    <div>
                        <span className="font-semibold text-md">Total</span>
                        <br />
                        <span className="text-xs text-gray-600">
                            {table.getFilteredRowModel().rows.length} records
                        </span>
                    </div>
                ),
            },
            // {
            //     accessorKey: 'uuid',
            //     header: 'Employee ID',
            //     cell: ({ row }) => (
            //         <span className="text-sm text-gray-600">{row.original.uuid}</span>
            //     ),
            //     enableColumnFilter: false,
            //     enableGrouping: false,
            //     meta: {
            //         cellClassName: "text-left",
            //         className: "text-sm",
            //     },
            //     footer: () => null,
            // },
            {
                accessorKey: 'month_year',
                header: 'Month-Year',
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {
                    cellClassName: "text-left",
                },
                footer: () => null,
            },
            {
                accessorKey: 'working_days',
                header: 'Working Days',
                cell: ({ row }) => {
                    const isEditing = editingCell?.uuid === row.original.uuid && editingCell?.month_year === row.original.month_year;
                    const currentValue = isEditing ? editingCell.value : (Number(row.original.working_days) || 0);
                    
                    if (isEditing) {
                        return (
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max="31"
                                    value={currentValue}
                                    onChange={(e) => setEditingCell({
                                        ...editingCell!,
                                        value: Number(e.target.value)
                                    })}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                                <button
                                    onClick={() => handleSave()}
                                    disabled={isSaving}
                                    className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50 transition-colors"
                                >
                                    {isSaving ? '...' : '✓'}
                                </button>
                                <button
                                    onClick={() => handleCancel()}
                                    disabled={isSaving}
                                    className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:opacity-50 transition-colors"
                                >
                                    ✕
                                </button>
                            </div>
                        );
                    }
                    
                    return (
                        <div className="flex items-center justify-between">
                            <span>{currentValue}</span>
                            <button
                                onClick={() => {
                                    handleEdit(row.original);
                                }}
                                className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                            >
                                Edit
                            </button>
                        </div>
                    );
                },
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="font-semibold">
                        {table.getFilteredRowModel().rows.reduce((total, row) => total + (Number(row.original.working_days) || 0), 0)}
                    </span>
                ),
            },
        ],
        [editingCell, isSaving, handleEdit, handleSave, handleCancel]
    );

    useEffect(() => {
        fetchWorkingDaysData();
    }, []);

    const fetchWorkingDaysData = async () => {
        try {
            setIsLoading(true);
            console.log('Fetching working days data...');
            
            const data = await getWorkingDaysData();
            console.log('Fetched data:', data);
            console.log('Data length:', data?.length);
            setWorkingDaysData(data || []);
        } catch (error: any) {
            console.error('Error fetching working days data:', error);
            setError(`Failed to load working days data: ${error?.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <div className="loading">Loading working days data...</div>;
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    return (
        <>
            <main>
                <PageTitle title="Working Days" />
                <Breadcrums />
                <section className={sectionClass}>

                    
                    {!isLoading && workingDaysData && workingDaysData.length > 0 && (
                        <TanstackTable 
                            data={workingDaysData} 
                            columns={columns} 
                            downloadBtn={true} 
                        />
                    )}
                    {!isLoading && workingDaysData.length <= 0 && (
                        <div className="my-5">No working days records found</div>
                    )}
                    {isLoading && <div className="my-5">Loading... </div>}
                    {error && <div className="my-5 text-red-600">Error: {error}</div>}
                </section>
            </main>
        </>
    );
};

export default WorkingDays;